import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Loader2, MapPin, Truck, User } from "lucide-react";
import { adminGet } from "./utils";

function pad(n) { return String(n).padStart(2, "0"); }
function ymd(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }

const STATUS_COLORS = {
  held: "bg-amber-100 text-amber-900 border-amber-300",
  pending_payment: "bg-blue-100 text-blue-900 border-blue-300",
  insurance_pending: "bg-purple-100 text-purple-900 border-purple-300",
  in_progress: "bg-emerald-100 text-emerald-900 border-emerald-300",
};

export function CalendarPanel({ auth }) {
  const [cursor, setCursor] = useState(() => new Date());
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);

  const monthStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
  const firstDayIdx = monthStart.getDay(); // 0 = Sunday

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const from = ymd(monthStart);
        const to = ymd(monthEnd);
        const d = await adminGet(auth, `rides/upcoming?from_date=${from}&to_date=${to}`);
        setRides(d.rides || []);
      } catch (err) {
        toast.error(err.message || "Failed to load calendar");
      } finally {
        setLoading(false);
      }
    })();
  }, [auth, cursor]); // eslint-disable-line react-hooks/exhaustive-deps

  const ridesByDate = useMemo(() => {
    const m = {};
    for (const r of rides) {
      (m[r.trip_date] = m[r.trip_date] || []).push(r);
    }
    for (const k of Object.keys(m)) {
      m[k].sort((a, b) => (a.trip_time || "").localeCompare(b.trip_time || ""));
    }
    return m;
  }, [rides]);

  const cells = [];
  for (let i = 0; i < firstDayIdx; i++) cells.push(null);
  for (let d = 1; d <= monthEnd.getDate(); d++) {
    cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
  }

  const today = ymd(new Date());
  const selectedRides = selectedDate ? (ridesByDate[selectedDate] || []) : [];

  return (
    <div className="space-y-4" data-testid="calendar-panel">
      <div className="bg-white border border-brand-navy/10 rounded-2xl p-5 flex items-center justify-between">
        <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
                data-testid="cal-prev" className="p-2 rounded-full hover:bg-brand-surfaceAlt">
          <ChevronLeft className="w-5 h-5 text-brand-deep" />
        </button>
        <div className="text-center">
          <div className="text-[10px] uppercase tracking-[0.22em] text-brand-green font-semibold">Upcoming rides</div>
          <div className="font-serif text-2xl text-brand-deep" data-testid="cal-month">
            {cursor.toLocaleString("en-US", { month: "long", year: "numeric" })}
          </div>
          <div className="text-xs text-brand-muted mt-1">{loading ? "Loading…" : `${rides.length} ride${rides.length === 1 ? "" : "s"} this month`}</div>
        </div>
        <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
                data-testid="cal-next" className="p-2 rounded-full hover:bg-brand-surfaceAlt">
          <ChevronRight className="w-5 h-5 text-brand-deep" />
        </button>
      </div>

      <div className="bg-white border border-brand-navy/10 rounded-2xl overflow-hidden">
        <div className="grid grid-cols-7 text-[10px] uppercase tracking-[0.18em] font-semibold text-brand-muted bg-brand-surfaceAlt/60">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div key={d} className="px-2 py-2 text-center">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((day, i) => {
            if (!day) return <div key={`e${i}`} className="min-h-[90px] border-t border-l border-brand-navy/10 bg-brand-surfaceAlt/20" />;
            const iso = ymd(day);
            const dayRides = ridesByDate[iso] || [];
            const isToday = iso === today;
            const isSelected = selectedDate === iso;
            return (
              <button
                key={iso}
                onClick={() => setSelectedDate(iso === selectedDate ? null : iso)}
                data-testid={`cal-day-${iso}`}
                className={`min-h-[90px] p-1.5 border-t border-l border-brand-navy/10 text-left transition ${isSelected ? "bg-emerald-50 ring-2 ring-emerald-500 ring-inset" : "hover:bg-brand-surfaceAlt/40"} ${isToday ? "bg-amber-50/50" : ""}`}
              >
                <div className={`text-xs font-semibold mb-1 ${isToday ? "text-brand-green" : "text-brand-deep"}`}>{day.getDate()}</div>
                <div className="space-y-0.5">
                  {dayRides.slice(0, 3).map((r) => (
                    <div key={r.id} className={`text-[10px] px-1.5 py-0.5 rounded truncate border ${STATUS_COLORS[r.status] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
                      <span className="font-mono">{r.trip_time?.slice(0, 5)}</span> {r.member_name?.split(" ")[0]}
                    </div>
                  ))}
                  {dayRides.length > 3 && (
                    <div className="text-[10px] text-brand-muted font-semibold">+{dayRides.length - 3} more</div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {selectedDate && (
        <div className="bg-white border border-brand-navy/10 rounded-2xl p-5" data-testid="cal-day-detail">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-brand-green font-semibold">Rides on</div>
              <div className="font-serif text-xl text-brand-deep">
                {new Date(selectedDate + "T00:00:00").toLocaleString("en-US", { weekday: "long", month: "long", day: "numeric" })}
              </div>
            </div>
            <button onClick={() => setSelectedDate(null)} className="text-xs text-brand-muted hover:text-brand-navy">Close</button>
          </div>
          {selectedRides.length === 0 ? (
            <div className="text-sm text-brand-muted text-center py-6">No rides scheduled.</div>
          ) : (
            <div className="space-y-2">
              {selectedRides.map((r) => (
                <div key={r.id} className="border border-brand-navy/10 rounded-xl p-3 flex items-start gap-3" data-testid={`cal-ride-${r.id}`}>
                  <div className="font-mono text-xs text-brand-deep bg-brand-surfaceAlt px-2 py-1 rounded shrink-0">
                    {r.trip_time?.slice(0, 5)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-brand-deep text-sm inline-flex items-center gap-1"><User className="w-3 h-3" /> {r.member_name}</span>
                      <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full border ${STATUS_COLORS[r.status] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
                        {r.status?.replace(/_/g, " ")}
                      </span>
                      {r.driver_name && (
                        <span className="text-[10px] text-emerald-800 bg-emerald-100 px-2 py-0.5 rounded-full inline-flex items-center gap-1"><Truck className="w-3 h-3" /> {r.driver_name}</span>
                      )}
                    </div>
                    <div className="text-xs text-brand-muted mt-1 truncate">
                      <MapPin className="w-3 h-3 inline text-emerald-600 mr-0.5" /> {r.pickup}
                    </div>
                    <div className="text-xs text-brand-muted truncate">→ {r.dropoff}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
